"use client"

import { useCallback, useId, useMemo, useRef, useState } from "react"
import type { Sound } from "@/lib/supabase"
import type { SubcollectionConfig } from "@/lib/category-data"
import SoundCard from "@/components/ui/sound-card"

interface CategorySubcollectionTabsProps {
  categoryName: string
  sounds: Sound[]
  subcollections: SubcollectionConfig[]
}

const ALL_TAB_ID = "all"

function matchesSubcollection(sound: Sound, sub: SubcollectionConfig): boolean {
  const haystack = [
    sound.title,
    sound.description ?? "",
    ...(sound.tags ?? []),
  ]
    .join(" ")
    .toLowerCase()
  return sub.keywords.some((k) => haystack.includes(k.toLowerCase()))
}

// SoundCard is a full-width ROW (play control, title, meta, download,
// like), not a card: PRODUCT.md's second rule is that the row is the unit
// of a list and density changes type size, never chrome. Rendered four to
// a line the row got ~280px, which left the title about 30px and truncated
// every one of them to a single character. This is the old card grid that
// predates the redesign; it stacks as one column so appended rows continue
// the same hairline list the page already renders above.

export default function CategorySubcollectionTabs({
  categoryName,
  sounds,
  subcollections,
}: CategorySubcollectionTabsProps) {
  const [activeId, setActiveId] = useState<string>(ALL_TAB_ID)
  const baseId = useId()
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  // Pre-compute counts so empty tabs aren't shown to users.
  const counts = useMemo(() => {
    const map: Record<string, number> = { [ALL_TAB_ID]: sounds.length }
    for (const sub of subcollections) {
      map[sub.id] = sounds.filter((s) => matchesSubcollection(s, sub)).length
    }
    return map
  }, [sounds, subcollections])

  const visibleSubs = subcollections.filter((s) => (counts[s.id] ?? 0) > 0)

  const filtered = useMemo(() => {
    if (activeId === ALL_TAB_ID) return sounds
    const sub = subcollections.find((s) => s.id === activeId)
    if (!sub) return sounds
    return sounds.filter((s) => matchesSubcollection(s, sub))
  }, [activeId, sounds, subcollections])

  const tabs = useMemo(
    () => [
      { id: ALL_TAB_ID, label: "All", count: counts[ALL_TAB_ID] ?? sounds.length },
      ...visibleSubs.map((s) => ({ id: s.id, label: s.label, count: counts[s.id] ?? 0 })),
    ],
    [counts, sounds.length, visibleSubs]
  )

  const tabId = useCallback((id: string) => `${baseId}-tab-${id}`, [baseId])
  const panelId = `${baseId}-panel`

  // Roving tabindex: ArrowLeft/Right/Home/End move focus AND activate the
  // adjacent tab (single-select tabs, so focus and selection stay in sync).
  const handleTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      let nextIndex: number | null = null
      if (event.key === "ArrowRight") {
        nextIndex = (index + 1) % tabs.length
      } else if (event.key === "ArrowLeft") {
        nextIndex = (index - 1 + tabs.length) % tabs.length
      } else if (event.key === "Home") {
        nextIndex = 0
      } else if (event.key === "End") {
        nextIndex = tabs.length - 1
      } else {
        return
      }
      event.preventDefault()
      const next = tabs[nextIndex]
      setActiveId(next.id)
      tabRefs.current[nextIndex]?.focus()
    },
    [tabs]
  )

  // No-op when there isn't enough variety to warrant tabs.
  if (visibleSubs.length === 0) {
    return (
      <>
        <div className="flex items-center justify-between mb-8">
          <h2 className="font-display text-lg font-semibold tracking-tight text-[var(--tls-fg-strong)]">
            <span className="tabular-nums">{sounds.length.toLocaleString("en-US")}</span> {categoryName} sounds
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-0">
          {sounds.map((sound) => (
            <SoundCard key={sound.id} sound={sound} showCategory={false} disableEntryAnimation />
          ))}
        </div>
      </>
    )
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-display text-lg font-semibold tracking-tight text-[var(--tls-fg-strong)]">
          <span className="tabular-nums">{filtered.length.toLocaleString("en-US")}</span> {categoryName} sounds
        </h2>
      </div>

      <div
        role="tablist"
        aria-label={`${categoryName} subcollections`}
        className="flex flex-wrap gap-2 mb-8 border-b border-[color:var(--tls-border-subtle)] pb-3"
      >
        {tabs.map((tab, index) => {
          const isActive = activeId === tab.id
          return (
            <button type="button"
              key={tab.id}
              ref={(el) => { tabRefs.current[index] = el }}
              id={tabId(tab.id)}
              role="tab"
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveId(tab.id)}
              onKeyDown={(e) => handleTabKeyDown(e, index)}
              className={`inline-flex items-center gap-2 rounded-[var(--radius-pill)] border px-4 py-1.5 text-sm transition-[background-color,border-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)] ${
                isActive
                  ? "border-[color:var(--tls-brand)] bg-[color-mix(in_srgb,var(--tls-brand)_12%,transparent)] text-[var(--tls-fg-strong)]"
                  : "border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] text-[var(--tls-fg-muted)] hover:border-[color:var(--tls-border-strong)] hover:text-[var(--tls-fg-strong)]"
              }`}
            >
              <span>{tab.label}</span>
              <span className="text-xs text-[var(--tls-fg-subtle)]">{tab.count}</span>
            </button>
          )
        })}
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabId(activeId)}
        tabIndex={0}
        className="grid grid-cols-1 gap-0"
      >
        {filtered.map((sound) => (
          <SoundCard key={sound.id} sound={sound} showCategory={false} disableEntryAnimation />
        ))}
      </div>
    </>
  )
}
